import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";

const AcademyList = ({ category, title, intro, headerImg, tamilSubtitle }) => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/public/departments?category=${category}`).then(r => setItems(r.data));
  }, [category]);

  const isMusic = category === "music";
  const seoTitle = isMusic
    ? "Musikakademie – Mirudangam, Violine, Veenai & mehr"
    : "Tanzakademie – Bharatanatyam, Bollywood, Kuthu, HipHop";
  const seoDesc = isMusic
    ? "Karnatische Musikausbildung mit Mirudangam als Hauptfokus. Klassischer und moderner Musikunterricht für tamilische Jugendliche in Wil SG."
    : "Tanzkurse für Bharatanatyam, Bollywood, Kuthu und HipHop. Tamilische Tanzakademie für alle Levels in Wil SG.";
  const seoPath = isMusic ? "/musikakademie" : "/tanzakademie";

  return (
    <div data-testid={`academy-${category}-page`}>
      <Seo title={seoTitle} description={seoDesc} path={seoPath} image={headerImg} />
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <img src={headerImg} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 w-full">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">{t("hero.eyebrow")}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] max-w-3xl text-white">{title}</h1>
          {tamilSubtitle && (
            <div className="font-tamil text-[hsl(var(--brand-gold-soft))] text-2xl md:text-3xl mt-3">{tamilSubtitle}</div>
          )}
          <p className="text-white/80 mt-6 max-w-2xl">{intro}</p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((d) => (
            <Link key={d.id} to={`/fachbereich/${d.slug}`} className="card-academy rounded-lg overflow-hidden group relative" data-testid={`dept-${d.slug}`}>
              {d.featured && (
                <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-primary text-primary-foreground px-2.5 py-1 rounded-full">
                  Fokus
                </div>
              )}
              <div className="aspect-[4/3] overflow-hidden">
                <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className={`text-xs uppercase tracking-[0.2em] ${d.status === "active" ? "text-primary" : "text-accent-warm"}`}>
                    {d.status === "active" ? t("department.active") : t("department.comingSoon")}
                  </div>
                  {d.price_per_lesson > 0 && (
                    <div className="text-xs text-muted-foreground">CHF {d.price_per_lesson} / {t("department.pricePerLesson")}</div>
                  )}
                </div>
                <div className="font-serif-display text-2xl mb-2">{d.name}</div>
                {d.levels && d.levels.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {d.levels.map(l => (
                      <span key={l} className="text-[9px] sm:text-[10px] uppercase tracking-[0.12em] border border-border rounded-full px-1.5 sm:px-2 py-0.5 text-muted-foreground whitespace-nowrap">{l}</span>
                    ))}
                  </div>
                )}
                <div className="text-sm text-muted-foreground line-clamp-2">{d.description}</div>
                <div className="mt-4 inline-flex items-center gap-2 text-primary text-sm group-hover:gap-3 transition-all">
                  {t("cta.readMore")} <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AcademyList;
